import React, { useState, useEffect, useCallback } from "react";
import { Container, Row, Col, Form, Card, Button, Breadcrumb, Table, Modal, Badge, Pagination } from "react-bootstrap";
import "bootstrap-icons/font/bootstrap-icons.css";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { getBackdateConfig } from "../../api/backdateConfig";
import { createBackdateRequest, getMyBackdateRequests } from "../../api/backdateRequest";

const ITEMS_PER_PAGE = 10;
const REASON_PREVIEW_LENGTH = 40;
const STATUS_VARIANT = { PENDING: "warning", APPROVED: "success", REJECTED: "danger" };

const fmtDate = (d) => {
    if (!d) return "-";
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(d);
    if (m) return `${m[3]}-${m[2]}-${m[1]}`;
    const dt = new Date(d);
    if (isNaN(dt)) return d;
    return `${String(dt.getDate()).padStart(2, "0")}-${String(dt.getMonth() + 1).padStart(2, "0")}-${dt.getFullYear()}`;
};

const toIso = (d) => {
    const z = new Date(d.getTime() - d.getTimezoneOffset() * 60000);
    return z.toISOString().slice(0, 10);
};

// A reason is meaningful only if it has at least one letter
// (rejects "12345", "!!!@@@", "123-456" etc.)
const isValidReason = (value) => /[a-zA-Z]/.test(value);

const BackdateRequestPage = () => {
    const token = localStorage.getItem("token");

    const [maxDays, setMaxDays] = useState(7);
    const [reportDate, setReportDate] = useState("");
    const [reason, setReason] = useState("");
    const [errors, setErrors] = useState({});
    const [submitting, setSubmitting] = useState(false);

    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);

    const [search, setSearch] = useState("");
    const [statusFilter, setStatusFilter] = useState("ALL");
    const [currentPage, setCurrentPage] = useState(1);

    const [showReasonModal, setShowReasonModal] = useState(false);
    const [reasonText, setReasonText] = useState("");

    // latest date that still NEEDS a request = today - maxDays - 1
    const latestRequestable = (() => {
        const d = new Date();
        d.setHours(0, 0, 0, 0);
        d.setDate(d.getDate() - (maxDays + 1));
        return toIso(d);
    })();

    const load = useCallback(() => {
        setLoading(true);
        getMyBackdateRequests(token)
            .then((r) => setRows(r.data || []))
            .catch(() => toast.error("Failed to load your requests."))
            .finally(() => setLoading(false));
    }, [token]);

    useEffect(() => {
        if (!token) return;
        getBackdateConfig(token)
            .then((r) => { if (r.data?.maxBackdateDays) setMaxDays(r.data.maxBackdateDays); })
            .catch(() => { });
        load();
    }, [token, load]);

    const validate = () => {
        const e = {};
        if (!reportDate) e.reportDate = "Please select a date";
        else if (reportDate > latestRequestable)
            e.reportDate = `Select a date older than ${maxDays} days. Recent dates can be reported directly.`;
        const r = reason.trim();
        if (!r) e.reason = "Reason is required";
        else if (r.length < 5) e.reason = "Reason must be at least 5 characters";
        else if (r.length > 500) e.reason = "Reason cannot exceed 500 characters";
        else if (!isValidReason(r))
            e.reason = "Reason must contain meaningful text (not only numbers or special characters)";
        setErrors(e);
        return Object.keys(e).length === 0;
    };

    const submit = async (ev) => {
        ev.preventDefault();
        if (!validate()) return;
        setSubmitting(true);
        try {
            await createBackdateRequest({ reportDate, reason: reason.trim() }, token);
            toast.success("Request sent to admin.");
            setReportDate("");
            setReason("");
            setErrors({});
            setCurrentPage(1);
            load();
        } catch (err) {
            toast.error(err.response?.data?.message || "Failed to send request.");
        } finally {
            setSubmitting(false);
        }
    };

    // ── filter + paginate (latest first, as returned by the server) ──
    let filtered = [...rows];
    if (statusFilter !== "ALL") filtered = filtered.filter((r) => r.status === statusFilter);
    if (search.trim()) {
        // accept 02/10/2026 or 02.10.2026 as well as 02-10-2026
        const q = search.trim().toLowerCase().replace(/[/.]/g, "-");
        filtered = filtered.filter((r) =>
            [
                fmtDate(r.reportDate),                      // Report Date, e.g. 02-10-2026
                fmtDate(r.createdAt),                       // Requested On
                r.decidedAt ? fmtDate(r.decidedAt) : "",    // Decided On
                r.reason,
                r.decidedBy,
            ].some((v) => (v || "").toLowerCase().includes(q))
        );
    }
    const totalPages = Math.ceil(filtered.length / ITEMS_PER_PAGE);
    const paginated = filtered.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);

    return (
        <Container fluid className="p-4">
            <style>{`
        .lc-breadcrumb a { text-decoration: none !important; color: #2E5CB8 !important; }
        .lc-breadcrumb .breadcrumb-item.active { color: #6c757d !important; }
        .pagination .page-item.active .page-link { background-color: #2E5CB8 !important; border-color: #2E5CB8 !important; }
        .form-control.is-invalid,
        .form-control.is-invalid:focus {
          border-color: #dee2e6 !important;
          background-image: none !important;
          box-shadow: none !important;
          padding-right: 0.75rem !important;
        }
      `}</style>
            <ToastContainer position="top-right" autoClose={3000} />

            <h4 className="page-main-heading" style={{ marginBottom: 12 }}>Backdated Reporting Request</h4>
            <Breadcrumb className="lc-breadcrumb">
                <Breadcrumb.Item href="#">Reporting</Breadcrumb.Item>
                <Breadcrumb.Item active>Backdated Reporting Request</Breadcrumb.Item>
            </Breadcrumb>
            <p className="text-muted mb-3" style={{ fontSize: 13 }}>
                Daily reports can be submitted for today and up to {maxDays} days back. To fill an older date,
                raise a request here. Your request goes directly to the admin.
            </p>

            {/* ── Request form ── */}
            <Row className="mb-4">
                <Col xl={12}>
                    <Card>
                        <Card.Body>
                            <Form onSubmit={submit} noValidate>
                                <Row className="g-3">
                                    <Col md={3}>
                                        <Form.Group>
                                            <Form.Label>Missed Date <span style={{ color: "red" }}>*</span></Form.Label>
                                            <Form.Control
                                                type="date"
                                                value={reportDate}
                                                max={latestRequestable}
                                                isInvalid={!!errors.reportDate}
                                                onClick={(e) => {
                                                    try { e.target.showPicker?.(); } catch (err) { /* picker already open or not supported */ }
                                                }}
                                                onChange={(e) => { setReportDate(e.target.value); setErrors((p) => ({ ...p, reportDate: "" })); }}
                                            />
                                            <Form.Control.Feedback type="invalid">{errors.reportDate}</Form.Control.Feedback>
                                        </Form.Group>
                                    </Col>
                                    <Col md={7}>
                                        <Form.Group>
                                            <Form.Label>Reason <span style={{ color: "red" }}>*</span></Form.Label>
                                            <Form.Control
                                                as="textarea"
                                                rows={2}
                                                maxLength={500}
                                                placeholder="Why do you need to fill the report for this date?"
                                                value={reason}
                                                isInvalid={!!errors.reason}
                                                onChange={(e) => {
                                                    const v = e.target.value;
                                                    setReason(v);
                                                    setErrors((p) => ({
                                                        ...p,
                                                        reason: v.trim() && !isValidReason(v.trim())
                                                            ? "Reason must contain meaningful text (not only numbers or special characters)"
                                                            : "",
                                                    }));
                                                }} />
                                            <Form.Control.Feedback type="invalid">{errors.reason}</Form.Control.Feedback>
                                            <small className="text-muted">{reason.length}/500</small>
                                        </Form.Group>
                                    </Col>
                                    <Col md={2} className="d-flex align-items-start" style={{ paddingTop: 32 }}>
                                        <Button type="submit" className="w-100" disabled={submitting}
                                            style={{ backgroundColor: "#2E5CB8", borderColor: "#2E5CB8" }}>
                                            {submitting ? "Sending..." : "Send Request"}
                                        </Button>
                                    </Col>
                                </Row>
                            </Form>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            {/* ── My requests table ── */}
            <Row className="mb-4">
                <Col xl={12}>
                    <Card>
                        <Card.Body>
                            <Row className="mb-3 d-flex justify-content-between align-items-center g-2">
                                <Col md="auto"><h5 className="mb-0">My Backdated Requests</h5></Col>
                                <Col xs="auto" className="d-flex gap-2">
                                    <div style={{ position: "relative", width: 320, flexShrink: 0 }}>
                                        <i className="bi bi-search"
                                            style={{
                                                position: "absolute", left: 12, top: "50%",
                                                transform: "translateY(-50%)", color: "#6c757d",
                                                fontSize: 14, pointerEvents: "none",
                                            }}
                                        ></i>
                                        <Form.Control
                                            type="text"
                                            placeholder="Search report date / reason"
                                            title="Search report date / reason"
                                            value={search}
                                            onChange={(e) => { setSearch(e.target.value); setCurrentPage(1); }}
                                            style={{ width: "100%", paddingLeft: 34, fontSize: 14 }}
                                        />
                                    </div>
                                    <Form.Select
                                        value={statusFilter}
                                        onChange={(e) => { setStatusFilter(e.target.value); setCurrentPage(1); }}
                                        style={{ width: 160, fontSize: 14, flexShrink: 0 }}
                                    >
                                        <option value="ALL">All Status</option>
                                        <option value="PENDING">Pending</option>
                                        <option value="APPROVED">Approved</option>
                                        <option value="REJECTED">Rejected</option>
                                    </Form.Select>
                                </Col>
                            </Row>

                            <div style={{ maxHeight: 450, overflow: "auto" }}>
                                <Table bordered hover>                <thead>
                                    <tr>
                                        {["Sr.No", "Report Date", "Requested On", "Reason", "Status", "Decided By", "Decided On"].map((h) => (
                                            <th key={h} style={{ backgroundColor: "#2E5CB8", color: "white", position: "sticky", top: 0, zIndex: 1 }}>{h}</th>
                                        ))}
                                    </tr>
                                </thead>
                                    <tbody>
                                        {loading && (
                                            <tr><td colSpan={7} className="text-center text-muted py-4">Loading...</td></tr>
                                        )}
                                        {!loading && paginated.length === 0 && (
                                            <tr><td colSpan={7} className="text-center text-muted py-4">No backdated requests found.</td></tr>
                                        )}
                                        {!loading && paginated.map((r, index) => (
                                            <tr key={r.id}>
                                                <td>{(currentPage - 1) * ITEMS_PER_PAGE + index + 1}</td>
                                                <td>{fmtDate(r.reportDate)}</td>
                                                <td>{fmtDate(r.createdAt)}</td>
                                                <td style={{ maxWidth: 260 }}>
                                                    <div className="d-flex align-items-center">
                                                        <span
                                                            style={{ minWidth: 0, overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis" }}
                                                            title={r.reason}
                                                        >
                                                            {r.reason || "-"}
                                                        </span>
                                                        {r.reason && r.reason.length > REASON_PREVIEW_LENGTH && (
                                                            <Button
                                                                variant="link"
                                                                size="sm"
                                                                className="p-0 ms-1 text-danger"
                                                                style={{ fontSize: 13, textDecoration: "none", whiteSpace: "nowrap" }}
                                                                onClick={() => { setReasonText(r.reason); setShowReasonModal(true); }}
                                                            >
                                                                Read more
                                                            </Button>
                                                        )}
                                                    </div>
                                                </td>
                                                <td><Badge bg={STATUS_VARIANT[r.status] || "secondary"}>{r.status}</Badge></td>
                                                <td>{r.decidedBy || "—"}</td>
                                                <td>{r.decidedAt ? fmtDate(r.decidedAt) : "—"}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            </div>
                            <div className="d-flex justify-content-end mt-3">
                                <Pagination>
                                    <Pagination.Prev onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))} disabled={currentPage === 1} />
                                    {[...Array(totalPages).keys()].map((n) => (
                                        <Pagination.Item key={n + 1} active={n + 1 === currentPage} onClick={() => setCurrentPage(n + 1)}>{n + 1}</Pagination.Item>
                                    ))}
                                    <Pagination.Next onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))} disabled={currentPage === totalPages || totalPages === 0} />
                                </Pagination>
                            </div>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            {/* ── Full reason ── */}
            <Modal show={showReasonModal} onHide={() => setShowReasonModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Reason</Modal.Title>
                </Modal.Header>
                <Modal.Body style={{ whiteSpace: "pre-wrap", wordBreak: "break-word", maxHeight: "60vh", overflowY: "auto" }}>
                    {reasonText}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" size="sm" onClick={() => setShowReasonModal(false)}>Close</Button>
                </Modal.Footer>
            </Modal>
        </Container>
    );
};

export default BackdateRequestPage;
